import { useEffect, useMemo, useState, useCallback } from 'react';
import type { ReactNode } from 'react';
import { UserManager, WebStorageStateStore, type User } from 'oidc-client-ts';
import { loadAuthConfig } from './auth-config';
import { loadMockAuth, isMockAuthAvailable } from '@shared/auth/mock-auth-loader';
import type { MockAuthUser } from '@shared/auth/mock-auth';
import { extractRoles } from '@shared/auth/extract-roles';
import { setCurrentToken, getJwtExp } from '@shared/auth/token-store';
import { safeReturnTo } from '@shared/auth/safe-return-to';
import { getRuntimeConfig } from '../config/runtime-config';
import { AuthContext } from '@shared/auth/AuthContext';
import type { AuthContextValue, AuthMode, AuthenticatedUser, RoleToken } from '@shared/auth/types';

/**
 * Mirrors `apps/support-console`'s `ConsoleAuthProvider`, which in turn
 * mirrors `@shared/auth/ZitadelProvider`. Every identity/role/token
 * PRIMITIVE is imported from `@shared` (mock-auth, extract-roles,
 * token-store, safe-return-to, AuthContext, types) -- this file adds
 * only the wiring, and holds no security logic of its own.
 *
 * The distinct `SESSION_STORAGE_KEY` is deliberate: an admin session
 * and a support session must not share storage. They are separate
 * OIDC clients with different admitted roles, and a shared key would
 * let a sign-in on one origin silently populate the other.
 */

const SESSION_STORAGE_KEY = 'nexsoss:admin-console-oidc-user-v1';

function toAuthenticatedUser(user: User | MockAuthUser): AuthenticatedUser {
  const profile = (user as User).profile ?? (user as MockAuthUser);
  const roles = extractRoles(user);
  return {
    id: String((profile as { sub?: string }).sub ?? (user as MockAuthUser).id),
    email: String((profile as { email?: string }).email ?? ''),
    displayName:
      String((profile as { name?: string }).name ?? '') ||
      String((profile as { email?: string }).email ?? '') ||
      'Unknown user',
    roles,
    organisation:
      String((profile as { org?: string }).org ?? (user as MockAuthUser).organisation ?? 'unknown'),
  };
}

export function AdminAuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthenticatedUser | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const runtime = getRuntimeConfig();
  const authMode: AuthMode = runtime.authMode;

  const userManagerRef = useMemo<UserManager | null>(() => {
    if (authMode !== 'zitadel') return null;
    const cfg = loadAuthConfig();
    return new UserManager({
      authority: cfg.authority,
      client_id: cfg.clientId,
      redirect_uri: cfg.redirectUri,
      post_logout_redirect_uri: cfg.postLogoutRedirectUri,
      response_type: 'code',
      scope: cfg.scope,
      loadUserInfo: true,
      userStore: new WebStorageStateStore({ store: window.sessionStorage }),
      metadata: {
        issuer: cfg.authority,
        authorization_endpoint: `${cfg.authority}/oauth/v2/authorize`,
        token_endpoint: `${cfg.authority}/oauth/v2/token`,
        end_session_endpoint: `${cfg.authority}/oidc/v1/end_session`,
        jwks_uri: `${cfg.authority}/oauth/v2/keys`,
        userinfo_endpoint: `${cfg.authority}/oidc/v1/userinfo`,
      },
    });
  }, [authMode]);

  useEffect(() => {
    let active = true;
    async function init() {
      try {
        if (authMode === 'mock') {
          if (!isMockAuthAvailable()) {
            console.error(
              'runtime-config.json sets authMode=mock but this is a production build; mock-auth is not available.',
            );
            setCurrentToken(null);
            return;
          }
          const mock = await loadMockAuth();
          const persisted = mock.p() as MockAuthUser | null;
          if (persisted) {
            const u = toAuthenticatedUser(persisted);
            const tok = mock.t(persisted);
            setUser(u);
            setToken(tok);
            setCurrentToken(tok);
            return;
          }
          setCurrentToken(null);
          return;
        }
        const manager = userManagerRef;
        if (!manager) return;
        const u = await manager.getUser();
        if (!active) return;
        if (u && !u.expired) {
          setUser(toAuthenticatedUser(u));
          setToken(u.access_token);
          setCurrentToken(u.access_token);
          window.sessionStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(u));
        } else {
          setCurrentToken(null);
        }
      } catch (err) {
        console.error('AdminAuthProvider init failed', err);
      } finally {
        if (active) setIsLoading(false);
      }
    }
    void init();
    return () => {
      active = false;
    };
  }, [authMode, userManagerRef]);

  const login = useCallback(
    async (returnTo?: string) => {
      if (authMode === 'mock') {
        if (!isMockAuthAvailable()) {
          throw new Error('mock-auth is not available in production builds');
        }
        // platform_admin is the ONLY role this console admits, so it is
        // also the only sensible mock default -- unlike the support
        // console, which defaults to its lowest-privilege role because
        // it admits three.
        const mock = await loadMockAuth();
        const mockUser = mock.g('nexsoss:platform_admin') as MockAuthUser;
        mock.s(mockUser);
        const tok = mock.t(mockUser);
        const u = toAuthenticatedUser(mockUser);
        setUser(u);
        setToken(tok);
        setCurrentToken(tok);
        window.location.assign(safeReturnTo(returnTo));
        return;
      }
      const manager = userManagerRef;
      if (!manager) throw new Error('OIDC UserManager not initialised');
      await manager.signinRedirect({ state: safeReturnTo(returnTo) });
    },
    [authMode, userManagerRef],
  );

  const logout = useCallback(async () => {
    if (authMode === 'mock') {
      const mock = isMockAuthAvailable() ? await loadMockAuth() : null;
      if (mock) mock.s(null);
      setUser(null);
      setToken(null);
      setCurrentToken(null);
      window.location.assign('/login');
      return;
    }
    const manager = userManagerRef;
    if (!manager) return;
    await manager.signoutRedirect();
    window.sessionStorage.removeItem(SESSION_STORAGE_KEY);
    setCurrentToken(null);
  }, [authMode, userManagerRef]);

  const signInMockRole = useCallback(
    (role: RoleToken) => {
      if (authMode !== 'mock') return;
      if (!isMockAuthAvailable()) return;
      void loadMockAuth().then((mock) => {
        const mockUser = mock.i(role) as MockAuthUser;
        const tok = mock.t(mockUser);
        setUser(toAuthenticatedUser(mockUser));
        setToken(tok);
        setCurrentToken(tok);
      });
    },
    [authMode],
  );

  useEffect(() => {
    if (!token) return;
    const exp = getJwtExp(token);
    if (exp === null) return;
    const REFRESH_LEAD_MS = 60_000;
    const delay = exp * 1000 - Date.now() - REFRESH_LEAD_MS;
    if (delay > 0) return; // silent-renew is out of scope -- see module docstring
    console.warn('[auth] session token is at/near expiry; sign in again to continue');
    setCurrentToken(null);
  }, [token]);

  const completeSignin = useCallback(
    async (url?: string) => {
      if (authMode !== 'zitadel') return;
      const manager = userManagerRef;
      if (!manager) throw new Error('OIDC UserManager not initialised');
      try {
        const u = await manager.signinRedirectCallback(url);
        if (!u) throw new Error('No user returned from sign-in callback');
        setUser(toAuthenticatedUser(u));
        setToken(u.access_token);
        setCurrentToken(u.access_token);
        window.sessionStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(u));
      } catch (err) {
        const stored = await manager.getUser();
        if (stored && !stored.expired) {
          setUser(toAuthenticatedUser(stored));
          setToken(stored.access_token);
          setCurrentToken(stored.access_token);
          window.sessionStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(stored));
        } else {
          throw err;
        }
      }
    },
    [authMode, userManagerRef],
  );

  const value: AuthContextValue = {
    isLoading,
    isAuthenticated: user !== null,
    user,
    roles: user?.roles ?? [],
    token,
    login,
    signInMockRole,
    completeSignin,
    logout,
    authMode,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
